import axios from 'axios';
import { BASE_URL } from '../constants/api-urls';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import { getCuratorAuthHeader } from '../utils/curatorApiAuth';

export type PreferredBillingMethod = 'card' | 'credits';

export type PreferredBillingState = {
  preferredBillingMethod: PreferredBillingMethod;
  creditsAllowed: boolean;
  creditsBlockedMessage?: string | null;
};

export type PreferredBillingUpdateResult = {
  preferredBillingMethod: PreferredBillingMethod;
  creditsAllowed?: boolean;
  message?: string;
};

const curatorHeaders = () => ({
  'Content-Type': 'application/json',
  ...getCuratorAuthHeader(),
});

export const fetchPreferredBilling = async (userId: string): Promise<PreferredBillingState> => {
  const response = await axios.get(
    `${BASE_URL}/auth0/user/${encodeURIComponent(userId)}/preferred-billing`,
    { headers: curatorHeaders() }
  );
  const data = response.data?.data || {};
  const method = data.preferredBillingMethod === 'credits' ? 'credits' : 'card';
  const creditsAllowed = data.creditsAllowed !== false;
  return {
    preferredBillingMethod: !creditsAllowed && method === 'credits' ? 'card' : method,
    creditsAllowed,
    creditsBlockedMessage: data.creditsBlockedMessage || null,
  };
};

/** @deprecated Prefer fetchPreferredBilling for credits gating. */
export const fetchPreferredBillingMethod = async (
  userId: string
): Promise<PreferredBillingMethod> => {
  const state = await fetchPreferredBilling(userId);
  return state.preferredBillingMethod;
};

export const updatePreferredBillingMethod = async (
  userId: string,
  preferredBillingMethod: PreferredBillingMethod
): Promise<PreferredBillingUpdateResult> => {
  const response = await axios.patch(
    `${BASE_URL}/auth0/user/${encodeURIComponent(userId)}/preferred-billing`,
    { preferredBillingMethod },
    { headers: curatorHeaders() }
  );
  const method = response.data?.data?.preferredBillingMethod;
  return {
    preferredBillingMethod: method === 'credits' ? 'credits' : 'card',
    creditsAllowed: response.data?.data?.creditsAllowed,
    message: response.data?.message,
  };
};

export type CreditsUpgradeResult = {
  alreadyOnPlan: boolean;
  subscriptionId: string;
  customerId?: string;
  priceId: string;
  amountCharged: number;
  currencyCode: string;
  cancelAtPeriodEnd: boolean;
  invoiceId?: string;
};

export const upgradePlanWithCredits = async (
  userId: string,
  priceId: string,
  currency?: string
): Promise<CreditsUpgradeResult> => {
  const response = await axios.post(
    `${BASE_URL}/auth0/user/${encodeURIComponent(userId)}/upgrade-with-credits`,
    { priceId, currency },
    { headers: curatorHeaders() }
  );
  return response.data.data;
};

export const getPreferredBillingErrorMessage = (error: unknown, fallback: string): string =>
  getApiErrorMessage(error, fallback);
